import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Handshake, ExternalLink } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const PartnersPage: React.FC = () => {
  const { t, localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const allPartners = useAdminStore((s) => s.partners);
  const galleryName = localize(settings?.galleryName) || 'Gallery Fotoatelier';

  const partners = useMemo(
    () => [...allPartners].filter((p) => p.isActive).sort((a, b) => a.displayOrder - b.displayOrder),
    [allPartners],
  );

  const categories = useMemo(
    () => [...new Set(partners.map((p) => localize(p.category)).filter(Boolean))],
    [partners, localize],
  );

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* Hero */}
      <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6 md:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
              {t.partners.title}
            </h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-black/40 dark:text-white/40 text-base sm:text-lg font-light max-w-xl leading-relaxed"
            >
              {t.partners.intro}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {partners.length === 0 ? (
        <section className="px-4 sm:px-6 md:px-24 pb-20">
          <div className="max-w-6xl mx-auto text-center py-20">
            <Handshake size={40} className="mx-auto text-black/10 dark:text-white/10 mb-4" />
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
              {t.partners.comingSoon}
            </p>
          </div>
        </section>
      ) : (
        <>
          {/* Logo Grid */}
          <section className="px-4 sm:px-6 md:px-24 pb-16">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-4 mb-16">
                {partners.map((partner, i) => (
                  <motion.a
                    key={partner._id}
                    href={partner.url || undefined}
                    target={partner.url ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05, duration: 0.4 }}
                    className="aspect-square rounded-2xl bg-[#fafafa] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5 flex items-center justify-center p-4 hover:border-black/15 dark:hover:border-white/15 hover:shadow-lg transition-all duration-300 group cursor-pointer"
                  >
                    {partner.logo ? (
                      <img src={partner.logo} alt={localize(partner.name)} className="w-full h-full object-contain opacity-40 group-hover:opacity-80 transition-opacity" />
                    ) : (
                      <p className="text-[8px] sm:text-[9px] font-bold uppercase tracking-wider text-black/30 dark:text-white/30 group-hover:text-black/60 dark:group-hover:text-white/60 transition-colors leading-tight text-center">
                        {localize(partner.name)}
                      </p>
                    )}
                  </motion.a>
                ))}
              </div>
            </div>
          </section>

          {/* Partners by Category */}
          {categories.map((category, catIdx) => (
            <section key={category} className="px-4 sm:px-6 md:px-24 pb-16 sm:pb-20">
              <div className="max-w-6xl mx-auto">
                <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-8">
                  <span className="text-[9px] font-bold tracking-[0.4em] text-black/20 dark:text-white/20 uppercase">{category}</span>
                </motion.div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {partners.filter((p) => localize(p.category) === category).map((partner, i) => (
                    <motion.div
                      key={partner._id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.1 + catIdx * 0.05, duration: 0.5 }}
                      className="group p-6 sm:p-8 rounded-2xl border border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a] hover:border-black/10 dark:hover:border-white/10 transition-all"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-black group-hover:text-white dark:group-hover:bg-white dark:group-hover:text-black transition-all duration-300 overflow-hidden">
                          {partner.logo ? (
                            <img src={partner.logo} alt="" className="w-full h-full object-contain p-1.5" />
                          ) : (
                            <Handshake size={20} />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-sm font-bold uppercase tracking-wider text-black dark:text-white">
                              {localize(partner.name)}
                            </h3>
                            {partner.url && (
                              <a href={partner.url} target="_blank" rel="noopener noreferrer" className="text-black/20 dark:text-white/20 hover:text-black dark:hover:text-white transition-colors">
                                <ExternalLink size={12} />
                              </a>
                            )}
                          </div>
                          <p className="text-xs text-black/40 dark:text-white/40 font-light leading-relaxed">
                            {localize(partner.description)}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          ))}
        </>
      )}

      {/* CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-[#fafafa] dark:bg-[#0a0a0a] border-t border-black/5 dark:border-white/5 transition-colors">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Handshake size={36} className="mx-auto text-black/15 dark:text-white/15 mb-6" />
            <h2 className="text-xl sm:text-3xl font-syncopate font-bold tracking-tighter text-black dark:text-white uppercase mb-3">
              {t.partners.becomePartner}
            </h2>
            <p className="text-black/40 dark:text-white/40 text-sm mb-8 max-w-md mx-auto font-light">
              {t.partners.becomePartnerText.replace('{name}', galleryName)}
            </p>
            <a href="/contact" className="inline-flex items-center gap-2 px-8 py-3.5 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
              {t.partners.getInTouch}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
